import { uniq } from "@-/com.npmjs.array-utils-ts";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { contentQueries } from "@-/dev.improve.haniverse.database.schemas.content.query";
import { staticQueries } from "@-/dev.improve.haniverse.database.schemas.static.query";
import type { HskVersionKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import type { GraphemeTier } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";
import { useMorphemeListStagesQuery } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import type { CardFilterFacts } from "@-/dev.improve.haniverse.lib.cards.filter";
import { useEffect, useMemo } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { isTransientNetworkError } from "@-/dev.improve.haniverse.lib.error-classification";

type StaticCardFilterFacts = Omit<CardFilterFacts, "stage">;

function asGraphemeTier(tier: number | null | undefined): GraphemeTier | null {
  return tier === 1 || tier === 2 || tier === 3 ? tier : null;
}

function selectHskLevel(
  hskVersion: HskVersionKey,
  prcData:
    | {
        minHsk2010Level: number | null;
        minHsk2021Level: number | null;
        minHsk2026Level: number | null;
      }
    | null
    | undefined,
): number | null {
  if (prcData == null) {
    return null;
  }
  switch (hskVersion) {
    case "hsk2": {
      return prcData.minHsk2010Level;
    }
    case "hsk3": {
      return prcData.minHsk2021Level;
    }
    case "hsk2026": {
      return prcData.minHsk2026Level;
    }
  }
}

/**
 * The immutable-DB half of a grapheme's {@link CardFilterFacts}: completed (content DB) plus
 * HSK level / TBCL level / tier / usage classification (static DB), read with one set-based query
 * per database for the whole grapheme set. Stage is deliberately excluded — it lives in the actor
 * DB and changes as the user studies, so it's merged in separately (see
 * {@link useCardFilterFactsByGrapheme}).
 */
export function useGetCardFilterStaticFactsQueryKey() {
  const { hskVersion } = useActorUser();
  // Capture the immutable database handles at render time so the non-render query function below
  // can issue narrow reads for each grapheme set. The compact facts map itself is the cached value;
  // avoiding nested `fetchQuery` calls prevents duplicate, full database rows from occupying a
  // second TanStack cache entry for five minutes.
  const getMeaningQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemeMeaningCard.findMany.useGetQueryKey({});
  const getGraphemeWithRefDataQueryKey = staticQueries(
    import.meta.location,
  ).grapheme.findMany.useGetQueryKey({
    with: { prcData: true, twData: true },
  });

  return (graphemes: readonly string[]) =>
    [
      "cardFilterStaticFacts",
      hskVersion,
      { getGraphemeWithRefDataQueryKey, getMeaningQueryKey },
      ...uniq([...graphemes]).sort(),
    ] as const;
}

export const [useCardFilterStaticFactsQueryOptions, cardFilterStaticFactsQueryFn] =
  createUseQueryOptions(
    useGetCardFilterStaticFactsQueryKey,
    async ({
      queryKey: [
        ,
        queryHskVersion,
        { getGraphemeWithRefDataQueryKey, getMeaningQueryKey },
        ...queryGraphemes
      ],
    }): Promise<ReadonlyMap<string, StaticCardFilterFacts>> => {
      const factsByGrapheme = new Map<string, StaticCardFilterFacts>(
        queryGraphemes.map((grapheme) => [
          grapheme,
          {
            completed: false,
            hskLevel: null,
            tbclLevel: null,
            tier: null,
            usageClassification: null,
          },
        ]),
      );
      if (hasLength(queryGraphemes, 0)) {
        return factsByGrapheme;
      }

      const meaningDatabase = getMeaningQueryKey({})[5].db;
      const staticDatabase = getGraphemeWithRefDataQueryKey({})[5].db;
      const [meaningCardRows, graphemeRows] = await Promise.all([
        meaningDatabase(import.meta.location).query.appGraphemeMeaningCard.findMany({
          columns: { completedAt: true, grapheme: true },
          where: { grapheme: { in: queryGraphemes } },
        }),
        staticDatabase(import.meta.location).query.grapheme.findMany({
          columns: { glyph: true },
          where: { glyph: { in: queryGraphemes } },
          with: {
            prcData: {
              columns: {
                minHsk2010Level: true,
                minHsk2021Level: true,
                minHsk2026Level: true,
                tier: true,
              },
            },
            twData: { columns: { tbclLevel: true, usageClassification: true } },
          },
        }),
      ]);

      for (const row of meaningCardRows) {
        const facts = factsByGrapheme.get(row.grapheme);
        if (facts !== undefined) {
          facts.completed = row.completedAt !== null;
        }
      }
      for (const row of graphemeRows) {
        const facts = factsByGrapheme.get(row.glyph);
        if (facts === undefined) {
          continue;
        }
        facts.hskLevel = selectHskLevel(queryHskVersion, row.prcData);
        facts.tier = asGraphemeTier(row.prcData?.tier);
        facts.tbclLevel = row.twData?.tbclLevel ?? null;
        facts.usageClassification = row.twData?.usageClassification ?? null;
      }
      return factsByGrapheme;
    },
  );

/**
 * Batched `grapheme → CardFilterFacts` for a whole grid/list: ONE content-DB read (completed), ONE
 * static-DB read (levels / tier / usage classification), and the shared batched stage query (actor
 * DB, live via its change subscription). Returns `undefined` until the immutable-DB facts settle so
 * callers can gate their first filtered render; stages merge in as they arrive (`"unadded"` maps to
 * a `null` stage, which `matchesCardFilters` treats as `"new"`).
 *
 * A deterministic FAILED static read is thrown to the nearest error boundary rather than left as a
 * permanent `undefined`. Callers gate filtering on this value, so a schema/query failure must not
 * silently masquerade as an empty catalog. A transient connection failure is different: callers
 * can keep rendering their unfiltered catalog while the QueryCache exposes the current failure in
 * the account drawer. Avoiding a render throw also prevents React Native's developer LogBox from
 * taking over the app for a recoverable network blip.
 */
export function useCardFilterFactsByGrapheme(
  graphemes: readonly string[],
  {
    enabled = true,
    refetchVersion,
  }: {
    enabled?: boolean | (() => boolean);
    /** Refresh after work was gated off, including a blur hidden by a frozen screen. */
    refetchVersion?: number;
  } = {},
): ReadonlyMap<string, CardFilterFacts> | undefined {
  const { data: staticFactsByGrapheme } = useQuery({
    ...useCardFilterStaticFactsQueryOptions(graphemes),
    enabled,
    throwOnError: (error) => !isTransientNetworkError(error),
  });
  const { data: stageByGrapheme, refetch: refetchStages } = useQuery({
    ...useMorphemeListStagesQuery(graphemes),
    enabled,
    placeholderData: keepPreviousData,
    refetchOnReconnect: enabled,
    refetchOnWindowFocus: enabled,
  });

  useEffect(() => {
    if (refetchVersion !== undefined && refetchVersion > 0) {
      // A tile-data observer can share this exact set and request the same foreground refresh.
      void refetchStages({ cancelRefetch: false });
    }
  }, [refetchStages, refetchVersion]);

  return useMemo(() => {
    if (staticFactsByGrapheme === undefined) {
      return undefined;
    }
    const factsByGrapheme = new Map<string, CardFilterFacts>();
    for (const [grapheme, staticFacts] of staticFactsByGrapheme) {
      const buttonStage = stageByGrapheme?.[grapheme];
      factsByGrapheme.set(grapheme, {
        ...staticFacts,
        stage: buttonStage === undefined || buttonStage === "unadded" ? null : buttonStage,
      });
    }
    return factsByGrapheme;
  }, [stageByGrapheme, staticFactsByGrapheme]);
}
